type RenderFn = (container: HTMLElement) => void | Promise<void>;
type Routes = Record<string, RenderFn>;

/**
 * Split a location hash into its route path and query string.
 *
 * `#/claim?code=ABC123` must render the /claim route, not fall through to home.
 * The QR codes the operator hands out carry exactly that shape, so matching the
 * raw hash against the route table would send every scanned code to the wrong
 * page.
 */
export function splitHash(hash: string): { path: string; query: URLSearchParams } {
  const raw = hash.replace(/^#/, '') || '/';
  const qIndex = raw.indexOf('?');
  if (qIndex === -1) return { path: raw, query: new URLSearchParams() };
  return {
    path: raw.slice(0, qIndex) || '/',
    query: new URLSearchParams(raw.slice(qIndex + 1)),
  };
}

/** Query parameters of the current hash route. */
export function hashQuery(): URLSearchParams {
  return splitHash(window.location.hash).query;
}

/** Route path of the current hash, query stripped. */
export function hashPath(): string {
  return splitHash(window.location.hash).path;
}

export class Router {
  constructor(private routes: Routes) {}

  start(): void {
    const root = document.getElementById('app');
    if (!root) throw new Error('No #app element');
    const render = async (): Promise<void> => {
      const { path } = splitHash(window.location.hash);
      const fn = this.routes[path] ?? this.routes['/'];
      if (!fn) throw new Error(`No route for ${path}`);
      root.innerHTML = '';
      await fn(root);
    };
    window.addEventListener('hashchange', () => { void render(); });
    void render();
  }
}
